import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, Table, Space, Typography, notification, Tag, Row, Col, Statistic, Button, Tooltip, Popover, Divider } from "antd";
import { DatasetService } from "../../../services/DatasetService";
import { usePermissions } from "../../../PermissionsProvider";
import { useAuth0 } from "@auth0/auth0-react";
import moment from "moment";
import { RequestViewButton } from "../../shared/button/Buttons";
import RequestViewModal from "../../Request/RequestViewModal";
import { ChatCompletionRequest } from "../../../model_gen/chat";
import { FinetuneDataset } from "../../../model_gen/finetuning";
import { CalendarOutlined, FileTextOutlined, CopyOutlined } from "@ant-design/icons";
import { ReactComponent as AiStars } from "../../../assets/svg/ai-creation.svg";

const { Text } = Typography;

interface AugmentationRequest {
  request_id: string;
  augmentation_status: string;
  augmentation_date_created: number;
  augmentation_date_executed: number;
  original_request_id: string;
  metadata: any;
}

const TagsCell: React.FC<{ metadata: any }> = ({ metadata }) => {
  const tags = useMemo(() => {
    try {
      // First check for dataset-specific tags
      if (metadata?._maitai_dataset_request_meta) {
        const meta = JSON.parse(metadata._maitai_dataset_request_meta as string);
        if (meta.tags?.length > 0) {
          return meta.tags;
        }
      }
      // Fall back to request metadata tags
      if (metadata?._maitai_request_meta) {
        const metaData = JSON.parse(metadata._maitai_request_meta as string);
        return metaData.tags || [];
      }
      return [];
    } catch (e) {
      console.error("Error parsing metadata:", e);
      return [];
    }
  }, [metadata]);

  return (
    <Space wrap>
      {tags.map((tag: string) => (
        <Tag key={tag} bordered={true}>
          {tag}
        </Tag>
      ))}
      {tags.length === 0 && (
        <span style={{ color: "#999" }}>No tags</span>
      )}
    </Space>
  );
};

const AugmentationDetail: React.FC = () => {
  const { id, augmentationId } = useParams<{ id: string; augmentationId: string }>();
  const { apiKey } = usePermissions();
  const { user } = useAuth0();
  const [requests, setRequests] = useState<AugmentationRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [dataset, setDataset] = useState<FinetuneDataset | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ChatCompletionRequest | null>(null);
  const [augmentationDetails, setAugmentationDetails] = useState<any | null>(null);
  const [originalRequestForModal, setOriginalRequestForModal] = useState<ChatCompletionRequest | null>(null);
  const [selectedRequests, setSelectedRequests] = useState<ChatCompletionRequest[]>([]);

  const styles = {
    statsCard: (color: string) => ({
      background: `linear-gradient(135deg, ${color}10 0%, #f8f8f8 100%)`,
      padding: "14px 16px",
      borderRadius: "12px",
      height: "100%",
      border: `1px solid ${color}30`,
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.03)",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
    }),
    monospaceFont: {
      fontFamily: "monospace",
      fontSize: "13px",
    },
  };

  useEffect(() => {
    if (id) {
      DatasetService.getDataset(Number(id), null, apiKey).subscribe(
        (data: FinetuneDataset) => {
          setDataset(data);
        },
        (error: any) => {
          console.error("Error fetching dataset:", error);
          notification.error({
            message: "Error",
            description: "Failed to fetch dataset details"
          });
        }
      );
    }
  }, [id, apiKey]);

  useEffect(() => {
    if (augmentationId) {
      DatasetService.getAugmentationDetails(augmentationId, null, apiKey).subscribe(
        (data: any) => {
          setAugmentationDetails(data);
          if (data.original_request) {
            // Convert the original request data into a ChatCompletionRequest format
            const originalRequest: ChatCompletionRequest = {
              request_id: data.original_request_id,
              params: data.original_request.params,
              metadata: data.original_request.metadata,
              // Add other required fields from ChatCompletionRequest
              company_id: data.company_id,
              application_id: data.application_id,
              date_created: data.date_created,
              inference_location: "SERVER",
              assistant: false,
              apply_corrections: false,
              evaluation_enabled: false
            };
            setOriginalRequestForModal(originalRequest);
          }
        },
        (error: any) => {
          console.error("Error fetching augmentation details:", error);
          notification.error({
            message: "Error",
            description: "Failed to fetch augmentation details"
          });
        }
      );
    }
  }, [augmentationId, apiKey]);

  const handleViewRequest = (request: ChatCompletionRequest) => {
    setSelectedRequest(request);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedRequest(null);
  };

  const fetchRequests = async () => {
    try {
      setIsLoading(true);
      const offset = (currentPage - 1) * pageSize;
      const response = await DatasetService.getAugmentationRequests(
        augmentationId!, 
        offset,
        pageSize,
        setIsLoading,
        apiKey
      ).toPromise();
      setRequests(response.items);
      setTotal(response.total);
    } catch (error) {
      console.error("Error fetching augmentation requests:", error);
      notification.error({
        message: "Error",
        description: "Failed to fetch augmentation requests"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [augmentationId, currentPage, pageSize, apiKey]);

  useEffect(() => {
    // Add global styles
    const style = document.createElement("style");
    style.innerHTML = `
      .augmentation-detail-table .ant-table-thead > tr > th {
        background: #e4f6f2 !important;
      }
      .augmentation-detail-table .ant-table-thead > tr > th::before {
        display: none !important;
      }
      .augmentation-detail-table .ant-table-thead {
        height: 61.5px;
      }
      .augmentation-detail-table .ant-table-row {
        height: 61.5px;
      }
      .augmentation-detail-table .ant-pagination {
        padding: 16px 0;
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 8px;
      }
      .augmentation-detail-table .ant-pagination-options {
        margin-left: 16px !important;
      }
      .augmentation-detail-table .ant-select-selector {
        border-radius: 6px !important;
      }
      .augmentation-detail-table .ant-table {
        border-radius: 8px;
        overflow: hidden;
      }
      .augmentation-detail-table .ant-table-tbody > tr.ant-table-row:hover > td {
        background-color: #f5f8ff !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const columns = [
    {
      title: "Date Created",
      dataIndex: "augmentation_date_created",
      key: "augmentation_date_created",
      width: "20%",
      render: (text: number) => moment(text).format("YYYY-MM-DD hh:mm:ss A"),
    },
    {
      title: (
        <div style={{ display: "flex", alignItems: "start" }}>
          Tags
          <AiStars
            style={{ width: "12px", height: "12px", color: "#8c8c8c" }}
          />
        </div>
      ),
      dataIndex: "metadata",
      key: "tags",
      width: "50%",
      render: (metadata: any) => <TagsCell metadata={metadata} />,
    },
    {
      title: "Request ID",
      key: "request_id",
      width: "15%",
      render: (_: any, record: AugmentationRequest) => {
        const requestId = record.request_id;
        const truncatedId = `...${requestId.slice(-8)}`;
        return (
          <Space style={{ width: "100%" }}>
            <Text style={styles.monospaceFont}>
              {truncatedId}
            </Text>
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(requestId);
                notification.success({
                  message: "Copied",
                  description: "Request ID copied to clipboard"
                });
              }}
            />
          </Space>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: "15%",
      render: (_: any, record: AugmentationRequest) => (
        <RequestViewButton
          apiKey={apiKey}
          request={record as unknown as ChatCompletionRequest}
          onClick={() => handleViewRequest(record as unknown as ChatCompletionRequest)}
          isEditable={false}>
          View
        </RequestViewButton>
      ),
    },
  ];

  return (
    <Card>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div style={{ fontSize: "24px", fontWeight: "600", marginBottom: "24px" }}>
          <Link to="/models/datasets" style={{ color: "#828282" }}>
            Datasets
            {" / "}
          </Link>
          <Link to={`/models/datasets/${id}`} style={{ color: "#828282" }}>
            {dataset?.name || "..."}
            {" / "}
          </Link>
          Augmentation Details
        </div>

        {/* Stats Overview */}
        <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
          <Col xs={24} sm={8}>
            <div style={styles.statsCard("#1890ff")}>
              <Statistic
                title={
                  <Typography.Text style={{ fontSize: "12px", color: "#8c8c8c" }}>
                    Original Request
                  </Typography.Text>
                }
                value={augmentationDetails?.original_request_id || "—"}
                formatter={(value) => {
                  if (value === "—") return value;
                  return originalRequestForModal ? (
                    <RequestViewButton
                      apiKey={apiKey}
                      request={originalRequestForModal}
                      onClick={() => {
                        setSelectedRequest(originalRequestForModal);
                        setIsModalVisible(true);
                      }}
                      isEditable={false}
                    >
                      View
                    </RequestViewButton>
                  ) : null;
                }}
                valueStyle={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#262626",
                  lineHeight: "1.4",
                }}
              />
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={styles.statsCard("#52c41a")}>
              <Statistic
                title={
                  <Typography.Text style={{ fontSize: "12px", color: "#8c8c8c" }}>
                    Date Created
                  </Typography.Text>
                }
                value={augmentationDetails?.date_created ? moment(augmentationDetails.date_created).format("MMM D, YYYY") : "—"}
                prefix={
                  <CalendarOutlined
                    style={{
                      color: "#52c41a",
                      marginRight: "8px",
                      fontSize: "16px",
                    }}
                  />
                }
                valueStyle={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#262626",
                  lineHeight: "1.4",
                }}
              />
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={styles.statsCard("#722ed1")}>
              <Statistic
                title={
                  <Typography.Text style={{ fontSize: "12px", color: "#8c8c8c" }}>
                    Total Augmentations
                  </Typography.Text>
                }
                value={augmentationDetails?.request_count || 0}
                prefix={
                  <FileTextOutlined
                    style={{
                      color: "#722ed1",
                      marginRight: "8px",
                      fontSize: "16px",
                    }}
                  />
                }
                valueStyle={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#262626",
                  lineHeight: "1.4",
                }}
              />
            </div>
          </Col>
        </Row>

        <Divider style={{ margin: "0" }} />

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="primary"
            disabled={selectedRequests.length === 0}
            onClick={async () => {
              try {
                setIsLoading(true);
                const payload = {
                  dataset_id: Number(id),
                  requests: selectedRequests.map((request) => ({
                    request_id: request.request_id,
                    metadata: request.metadata || {},
                  })),
                };

                await DatasetService.addDatasetRequests(
                  Number(id),
                  payload,
                  null,
                  apiKey
                ).toPromise();

                notification.success({
                  message: "Success",
                  description: `${selectedRequests.length} requests added to dataset`
                });

                // Clear selection after successful addition
                setSelectedRequests([]);
              } catch (error) {
                console.error("Error adding requests to dataset:", error);
                notification.error({
                  message: "Error",
                  description: "Failed to add requests to dataset"
                });
              } finally {
                setIsLoading(false);
              }
            }}>
            {selectedRequests.length === 0 
              ? "Add Requests to Dataset" 
              : `Add ${selectedRequests.length} ${selectedRequests.length === 1 ? "Request" : "Requests"} to Dataset`}
          </Button>
        </div>

        <Table
          rowKey="request_id"
          columns={[
            {
              title: "Date Created",
              dataIndex: "augmentation_date_created",
              key: "augmentation_date_created",
              width: "20%",
              render: (text: number) => moment(text).format("YYYY-MM-DD hh:mm:ss A"),
            },
            {
              title: (
                <div style={{ display: "flex", alignItems: "start" }}>
                  Tags
                  <AiStars
                    style={{ width: "12px", height: "12px", color: "#8c8c8c" }}
                  />
                </div>
              ),
              dataIndex: "metadata",
              key: "tags",
              width: "50%",
              render: (metadata: any) => <TagsCell metadata={metadata} />,
            },
            {
              title: "Request ID",
              key: "request_id",
              width: "15%",
              render: (_: any, record: AugmentationRequest) => {
                const requestId = record.request_id;
                const truncatedId = `...${requestId.slice(-8)}`;
                return (
                  <Space style={{ width: "100%" }}>
                    <Text style={styles.monospaceFont}>
                      {truncatedId}
                    </Text>
                    <Button
                      icon={<CopyOutlined />}
                      size="small"
                      type="text"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(requestId);
                        notification.success({
                          message: "Copied",
                          description: "Request ID copied to clipboard"
                        });
                      }}
                    />
                  </Space>
                );
              },
            },
            {
              title: "",
              key: "actions",
              width: "15%",
              render: (_: any, record: AugmentationRequest) => (
                <RequestViewButton
                  apiKey={apiKey}
                  request={record as unknown as ChatCompletionRequest}
                  onClick={() => handleViewRequest(record as unknown as ChatCompletionRequest)}
                  isEditable={false}>
                  View
                </RequestViewButton>
              ),
            },
          ]}
          dataSource={requests}
          loading={isLoading}
          className="augmentation-detail-table"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            onChange: (page, size) => {
              setCurrentPage(page);
              if (size) setPageSize(size);
            },
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
          rowSelection={{
            columnWidth: 40,
            selectedRowKeys: selectedRequests.map(r => r.request_id),
            onChange: (_, selectedRows) => {
              setSelectedRequests(selectedRows as ChatCompletionRequest[]);
            }
          }}
        />
      </Space>

      {selectedRequest && (
        <RequestViewModal
          request={selectedRequest}
          isVisible={isModalVisible}
          onClose={handleCloseModal}
          apiKey={apiKey}
        />
      )}
    </Card>
  );
};

export default AugmentationDetail; 